import React, { useEffect, useState } from 'react';
import { Gift, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { FormulaFormModal } from './FormulaFormModal';
import { CategoryManager } from './CategoryManager';
import {
    type PrestationCatalogItem,
    type PrestationCategory,
    type PrestationFormula,
    type ClientTypeScope,
    ITEM_BENEFIT_LABEL,
} from './prestationsTypes';

export function FormulesSection({ catalog, categories, onCategoriesChanged, audience }: { catalog: PrestationCatalogItem[]; categories: PrestationCategory[]; onCategoriesChanged: () => void; audience: ClientTypeScope }) {
    const [formulas, setFormulas] = useState<PrestationFormula[]>([]);
    // La formule doit pouvoir proposer une prestation dédiée à cet onglet OU une prestation "Tous" — jamais
    // une prestation réservée à l'autre public (ex: pas de "nettoyage mobilier restaurant" dans une formule
    // Particulier).
    const catalogForAudience = catalog.filter((c) => (c.client_type_scope || 'TOUS') === 'TOUS' || c.client_type_scope === audience);
    const visibleFormulas = formulas.filter((f) => (f.client_type_scope || 'TOUS') === 'TOUS' || f.client_type_scope === audience);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<PrestationFormula | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState('');

    const load = () => {
        setLoading(true);
        moduleApi.getPrestationFormulas()
            .then((data: any) => setFormulas(Array.isArray(data) ? data : []))
            .catch(() => setFormulas([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const handleDelete = async (id: string) => {
        setDeleteError('');
        setDeletingId(id);
        try {
            const res: any = await moduleApi.deletePrestationFormula(id);
            if (res?.error) { setDeleteError(res.error); return; }
            load();
        } catch (e: any) {
            setDeleteError(e?.message || 'Erreur lors de la suppression.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-4 pt-8 mt-8 border-t border-border">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--acc-bg)', color: 'var(--acc)' }}>
                        <Gift size={22} />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-content">Formules</h2>
                        <p className="text-sm text-content-muted mt-1">Des offres groupées avec engagement ou non (ex: "6 nettoyages de canapé dans l'année"), avec réduction et/ou occurrences offertes.</p>
                    </div>
                </div>
                <button onClick={() => { setEditing(null); setShowModal(true); }} disabled={catalogForAudience.length === 0}
                    className="h-9 px-4 inline-flex items-center gap-1.5 rounded-full text-[13px] font-bold text-white whitespace-nowrap shrink-0 self-start md:self-auto transition-opacity hover:opacity-90 disabled:opacity-50" style={{ background: 'var(--acc)' }}>
                    <Plus size={16} /> Nouvelle formule
                </button>
            </div>
            {catalogForAudience.length === 0 && (
                <p className="text-xs text-content-muted italic">Ajoutez d'abord au moins une prestation {audience === 'PRIVÉ' ? 'pour les particuliers' : 'pour les professionnels'} au catalogue ci-dessus pour pouvoir créer une formule.</p>
            )}
            {deleteError && <p className="text-xs text-red-500 font-semibold">{deleteError}</p>}

            {loading ? (
                <div className="py-10 text-center text-content-muted flex flex-col items-center gap-2">
                    <Loader2 size={24} className="animate-spin" />
                </div>
            ) : visibleFormulas.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-xl bg-surface/40">
                    <Gift size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucune formule {audience === 'PRIVÉ' ? 'pour les particuliers' : 'pour les professionnels'} configurée pour l'instant.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visibleFormulas.map((f) => (
                        <div key={f.id} className="rounded-xl border border-border bg-surface p-4 space-y-2.5 flex flex-col">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 space-y-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-content text-sm truncate">{f.name}</span>
                                        {!f.is_active && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-content-muted/10 text-content-muted">Inactive</span>}
                                        {f.engagement && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-400">Avec engagement</span>}
                                    </div>
                                    <p className="text-xs text-content-muted">{f.passages_libres ? `Passages libres sur ${f.validity_months} mois` : `${f.occurrences_total} occurrence${f.occurrences_total > 1 ? 's' : ''}`} · {f.items.length} prestation{f.items.length > 1 ? 's' : ''} incluse{f.items.length > 1 ? 's' : ''}</p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <button type="button" onClick={() => { setEditing(f); setShowModal(true); }} className="p-2 rounded-lg text-content-muted hover:text-content hover:bg-surface-soft transition-all" aria-label="Modifier"><Pencil size={14} /></button>
                                    <button type="button" onClick={() => handleDelete(f.id)} disabled={deletingId === f.id}
                                        className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-50" aria-label="Supprimer">
                                        {deletingId === f.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {[...f.items].sort((a, b) => Number(b.required) - Number(a.required)).map((it) => {
                                    const benefitLabel = ITEM_BENEFIT_LABEL(it);
                                    const hasBenefit = benefitLabel.length > 0;
                                    return (
                                        <span key={it.catalog_item_id}
                                            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg border ${hasBenefit ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:text-emerald-400' : 'bg-surface-soft border-border text-content-muted'}`}>
                                            {it.required && <span className="text-amber-600 dark:text-amber-400" title="Obligatoire à chaque passage">●</span>}
                                            {it.label}
                                            {hasBenefit && <span className="opacity-80 font-bold">· {benefitLabel}</span>}
                                        </span>
                                    );
                                })}
                            </div>
                            {f.items.some((it) => it.required) && (
                                <p className="text-[10px] text-content-muted flex items-center gap-1"><span className="text-amber-600 dark:text-amber-400">●</span> Obligatoire à chaque passage</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <FormulaFormModal initial={editing} catalog={catalogForAudience} categories={categories} defaultScope={audience} onCategoriesChanged={onCategoriesChanged} onClose={() => setShowModal(false)} onSaved={load} />
            )}
        </div>
    );
}

